import { useCallback, useRef } from 'react';

import { css, styled, tabClasses as muiTabClasses, Tabs as MuiTabs } from '@mui/material';
import { useTranslation } from '@kc-console/core';
import {
  IcAddSmallRegular,
  IcChevronLeftLargeRegular,
  IcChevronRightLargeRegular,
} from '@kep/kcds-icons';

import { useCascadeState } from '../../utils';
import { IconButtonGhost } from '../IconButtonGhost';
import { Link } from '../Link';
import { Tab, tabClasses } from '../Tab';
import { Tooltip } from '../Tooltip';
import { muiTabsClasses, tabsClasses } from './theme';

import type { MouseEvent } from 'react';
import type { TabScrollButtonProps } from '@mui/material';
import type { TabsOwnerState, TabsProps } from './types';

const ADD_BUTTON_VALUE = '__tabs_add_button__';

export default function Tabs(props: TabsProps) {
  const { t } = useTranslation();
  const {
    defaultValue,
    value: valueProp,
    onChange: onChangeProp,
    items,
    size = 'lg',
    addButton,
    alwaysShowScrollButtons = false,
    showItemDividers = false,
    slotProps,
    slots,
    ...others
  } = props;

  const { link: LinkSlotComponent = Link } = slots ?? {};
  const { link: linkSlotProps } = slotProps ?? {};

  const [value, onChange] = useCascadeState<string | null>({
    initialValue: defaultValue ?? null,
    value: valueProp,
    onChange: value => value !== null && onChangeProp?.(value),
  });

  const tabsRef = useRef<HTMLDivElement>(null);

  // 탭을 화면에 보이도록 스크롤하는 함수
  const scrollToTab = useCallback((tabValue: string) => {
    const tabsElement = tabsRef.current;
    if (!tabsElement) return;

    const scrollContainer = tabsElement.querySelector(`.MuiTabs-flexContainer`) as HTMLElement;
    if (!scrollContainer) return;

    // 해당 value를 가진 탭 버튼 찾기
    const tabButton = tabsElement.querySelector(`[value="${tabValue}"]`) as HTMLElement;
    if (!tabButton) return;

    // 탭이 화면에 보이도록 스크롤
    const scrollLeft = scrollContainer.scrollLeft;
    const tabOffsetLeft = tabButton.offsetLeft;
    const tabWidth = tabButton.offsetWidth;

    // 탭이 왼쪽에 숨겨져 있으면
    if (tabOffsetLeft < scrollLeft) {
      scrollContainer.scrollTo({ left: tabOffsetLeft - 16, behavior: 'smooth' });
    }
    // 탭이 오른쪽에 숨겨져 있으면
    else if (tabOffsetLeft + tabWidth > scrollLeft + scrollContainer.clientWidth) {
      scrollContainer.scrollTo({
        left: tabOffsetLeft + tabWidth - scrollContainer.clientWidth + 16,
        behavior: 'smooth',
      });
    }
  }, []);

  // 이전 탭으로 이동
  const goToPreviousTab = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const currentIndex = items.findIndex(item => item.value === value);
      if (currentIndex > 0) {
        const previousTab = items[currentIndex - 1];
        if (previousTab.onClick) {
          previousTab.onClick(event as unknown as MouseEvent<HTMLDivElement>, previousTab.value);
        } else {
          onChange(previousTab.value);
        }

        setTimeout(() => scrollToTab(previousTab.value), 50);
      }
    },
    [items, value, onChange, scrollToTab],
  );

  // 다음 탭으로 이동
  const goToNextTab = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const currentIndex = items.findIndex(item => item.value === value);
      if (currentIndex < items.length - 1) {
        const nextTab = items[currentIndex + 1];
        if (nextTab.onClick) {
          nextTab.onClick(event as unknown as MouseEvent<HTMLDivElement>, nextTab.value);
        } else {
          onChange(nextTab.value);
        }

        setTimeout(() => scrollToTab(nextTab.value), 50);
      }
    },
    [items, value, onChange, scrollToTab],
  );

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: string | null) => {
      if (newValue === ADD_BUTTON_VALUE) {
        addButton?.onClick();
        return;
      }

      onChange(newValue);
    },
    [addButton, onChange],
  );

  const ownerState: TabsOwnerState = {
    ...props,
    size,
    showItemDividers,
  };

  const currentIndex = items.findIndex(item => item.value === value);
  const isFirstTab = currentIndex === 0;
  const isLastTab = currentIndex === items.length - 1;

  return (
    <TabsRoot
      ref={tabsRef}
      variant="scrollable"
      scrollButtons={alwaysShowScrollButtons ? true : 'auto'}
      value={value}
      onChange={handleChange}
      ownerState={ownerState}
      slots={{
        scrollButtons: (scrollButtonProps: TabScrollButtonProps) => {
          const { direction } = scrollButtonProps;
          const isLeftSide = direction === 'left';
          const isDisabled = isLeftSide ? isFirstTab : isLastTab;

          return (
            <IconButtonGhost
              variant="secondary"
              size="lg"
              disabled={isDisabled}
              aria-label={isLeftSide ? t('ui:action.prev') : t('ui:action.next')}
              onClick={isDisabled ? undefined : isLeftSide ? goToPreviousTab : goToNextTab}
            >
              {isLeftSide ? <IcChevronLeftLargeRegular /> : <IcChevronRightLargeRegular />}
            </IconButtonGhost>
          );
        },
      }}
      {...others}
    >
      {items.map((item, index) => {
        const TabComponent = addButton && index === items.length - 1 ? TabsLastItemRoot : Tab;

        return (
          <TabComponent
            disabled={item.disabled}
            size={size}
            href={item.href}
            key={index}
            label={item.label}
            endAdornment={item.endAdornment ?? item.count}
            info={item.info}
            value={item.value}
            LinkComponent={LinkSlotComponent}
            onClick={(e: MouseEvent<HTMLDivElement>) => item.onClick?.(e, item.value)}
            {...linkSlotProps}
          />
        );
      })}
      {addButton && (
        <TabsAddButtonRoot
          addButtonSize={size}
          aria-label={t('common:common.action.add')}
          disabled={addButton.disabled}
          label={
            <Tooltip message={addButton.tooltip ?? ''}>
              <span>
                <IcAddSmallRegular />
              </span>
            </Tooltip>
          }
          size={size}
          value={ADD_BUTTON_VALUE}
        />
      )}
    </TabsRoot>
  );
}

const TabsAddButtonRoot = styled(Tab, {
  target: tabsClasses.addButton,
  shouldForwardProp: prop => prop !== 'addButtonSize',
})<{ addButtonSize: TabsOwnerState['size'] }>`
  width: ${({ addButtonSize }) => (addButtonSize === 'lg' ? '40px' : '32px')};
  min-width: ${({ addButtonSize }) => (addButtonSize === 'lg' ? '40px' : '32px')};
  margin-left: ${({ addButtonSize }) => (addButtonSize === 'lg' ? '-40px' : '-32px')};
  padding: ${({ addButtonSize }) => (addButtonSize === 'lg' ? '10px' : '6px')};
  background-color: ${({ theme }) => theme.color['surface/default']};
  box-shadow:
    inset 1px 0 ${({ theme }) => theme.color['border/weak/default']},
    inset 0 -1px ${({ theme }) => theme.color['border/weak/default']};

  /* 겹쳐진 마지막 탭으로 포인터 이벤트가 관통하지 않도록 비활성 버튼 자체가 이벤트 표면을 유지합니다. */
  &.${muiTabClasses.disabled} {
    pointer-events: auto;
  }

  .${tabClasses.itemLabel} {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
  }

  .kc-console-icon {
    display: block;
    width: ${({ addButtonSize }) => (addButtonSize === 'lg' ? '20px' : '16px')};
    min-width: ${({ addButtonSize }) => (addButtonSize === 'lg' ? '20px' : '16px')};
    height: ${({ addButtonSize }) => (addButtonSize === 'lg' ? '20px' : '16px')};
  }
`;

// 마지막 탭의 스크롤 영역에 추가 버튼 너비를 포함해 MUI가 두 요소를 한 번에 노출하도록 합니다.
const TabsLastItemRoot = styled(Tab)<{ size?: TabsOwnerState['size'] }>`
  padding-right: ${({ size }) => (size === 'lg' ? '56px' : '48px')};
`;

const TabsRoot = styled(MuiTabs, {
  target: tabsClasses.root,
})<{ ownerState: TabsOwnerState }>`
  position: relative;
  min-height: initial;

  &::before {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 1px;
    border-bottom: ${({ theme }) => theme.mixins.border('border/weak/default', 1)};
  }

  .${muiTabsClasses.flexContainer} {
    position: relative;
    gap: 0;
  }

  ${({ ownerState, theme }) =>
    ownerState.showItemDividers &&
    css`
      .${tabClasses.item}:not(.${tabsClasses.addButton}) {
        box-shadow:
          inset 1px 0 ${theme.color['border/weak/default']},
          inset 0 1px ${theme.color['border/weak/default']};
      }

      .${muiTabClasses.selected} + .${tabClasses.item}:not(.${tabsClasses.addButton}) {
        box-shadow: inset 0 1px ${theme.color['border/weak/default']};
      }
    `}

  .${muiTabsClasses.indicator} {
    display: none;
  }
`;
